import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Gauge, Boxes, Activity, LogOut, ArrowLeft,
  UserCog, ShieldAlert, CheckCircle2, BarChart3, ShieldCheck, FileText, Mail, Rocket,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { applyTheme } from "@/lib/theme";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { isGlobal } from "@/lib/roles";
import { Sidebar } from "@/components/portal/Sidebar";
import { BottomNav } from "@/components/portal/BottomNav";
import { Btn, Loader } from "@/components/portal/ui";
import { LanguageSwitcher } from "@/components/portal/LanguageSwitcher";
import { UsersManager } from "@/components/control/UsersManager";
import { CrossApprovals } from "@/components/control/CrossApprovals";
import { SecurityLog } from "@/components/control/SecurityLog";
import { PortalDetail } from "@/components/control/PortalDetail";
import { ControlReports } from "@/components/control/ControlReports";
import { ControlHero } from "@/components/control/ControlHero";
import { KPIRow } from "@/components/control/KPIRow";
import { RichPortalCard } from "@/components/control/RichPortalCard";
import { PortalHealthMatrix } from "@/components/control/PortalHealthMatrix";
import { TaskCenter } from "@/components/control/TaskCenter";
import { ActivityTimeline } from "@/components/control/ActivityTimeline";
import { SecurityPanel } from "@/components/control/SecurityPanel";
import { ControlDocuments } from "@/components/control/ControlDocuments";
import { EmailStatus } from "@/components/control/EmailStatus";
import { ProductionHealthMatrix } from "@/components/control/ProductionHealthMatrix";

function ControlLogin() {
  const { login } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/control")}`;
  };
  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm mb-6" style={{ color: "var(--text-muted)" }} data-testid="control-back"><ArrowLeft className="h-4 w-4" /> KEWAGPortal</Link>
        <div className="border p-8" style={{ background: "var(--surface)", borderColor: "var(--border)", borderRadius: "var(--radius)" }}>
          <div className="flex items-center gap-2 font-heading text-2xl font-bold" style={{ color: "var(--text)" }}>
            <Gauge className="h-6 w-6" style={{ color: "var(--accent)" }} /> Control OS
          </div>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("control.subtitle")}</p>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input data-testid="control-email" required type="email" placeholder={t("auth.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3.5 h-11 text-sm border outline-none" style={st} />
            <input data-testid="control-password" required type="password" placeholder={t("auth.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3.5 h-11 text-sm border outline-none" style={st} />
            <Btn type="submit" disabled={busy} className="w-full" data-testid="control-login-submit">{t("auth.signIn")}</Btn>
          </form>
          <div className="my-4 flex items-center gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} /> {t("auth.or")} <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>
          <Btn variant="outline" onClick={google} className="w-full" data-testid="control-google">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" /> {t("auth.continueGoogle")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default function ControlApp() {
  const { user, loading, logout } = useAuth();
  const { t, L } = useI18n();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [detailKey, setDetailKey] = useState(null);

  useEffect(() => {
    api.get("/portals/_control/config").then((r) => applyTheme(r.data.theme)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && isGlobal(user)) {
      api.get("/control/overview").then(({ data }) => setData(data));
      api.get("/control/activity").then(({ data }) => setActivity(data));
    }
  }, [user]);

  if (loading) return <Loader />;
  if (!user) return <ControlLogin />;
  if (!isGlobal(user))
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center" style={{ background: "var(--bg)" }}>
        <ShieldCheck className="h-10 w-10" style={{ color: "var(--accent)" }} />
        <p style={{ color: "var(--text)" }}>{t("control.noTeam")}</p>
        <Btn variant="outline" onClick={logout}><LogOut className="h-4 w-4" /> {t("nav.logout")}</Btn>
      </div>
    );

  const items = [
    { group: true, key: "g-overview", label: t("navgroup.overview") },
    { key: "overview", label: t("nav.overview"), icon: Gauge },
    { key: "portals", label: t("nav.portals"), icon: Boxes },
    { group: true, key: "g-management", label: t("navgroup.management") },
    { key: "approvals", label: t("control.approvals"), icon: CheckCircle2, badge: data?.totals?.pending },
    { key: "users", label: t("control.users"), icon: UserCog },
    { key: "documents", label: t("control.documents"), icon: FileText },
    { group: true, key: "g-insights", label: t("navgroup.insights") },
    { key: "activity", label: t("nav.activity"), icon: Activity },
    { key: "reports", label: t("nav.reports"), icon: BarChart3 },
    { key: "readiness", label: t("nav.readiness"), icon: Rocket },
    { key: "automations", label: L({ de: "Automationen", fr: "Automatisations", it: "Automazioni", en: "Automations" }), icon: Mail },
    { key: "security", label: t("control.security"), icon: ShieldAlert },
  ];

  const select = (k) => { setSection(k); setDetailKey(null); };
  const openDetail = (key) => { setDetailKey(key); };

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      <header className="h-16 border-b flex items-center sticky top-0 z-40" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="w-full px-4 sm:px-6 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold" style={{ color: "var(--text)" }}>
            <Gauge className="h-5 w-5" style={{ color: "var(--accent)" }} /> {t("control.title")}
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <LanguageSwitcher />
            <span className="hidden sm:block text-sm" style={{ color: "var(--text-muted)" }}>{user.name}</span>
            <Btn variant="ghost" onClick={logout} className="h-10 px-3" data-testid="control-logout"><LogOut className="h-4 w-4" /></Btn>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        <Sidebar items={items} active={section} onSelect={select} />
        <BottomNav items={items} active={section} onSelect={select} />
        <main className="flex-1 p-4 sm:p-8 pb-24 lg:pb-8 min-w-0">
          {detailKey ? (
            <PortalDetail portalKey={detailKey} onBack={() => setDetailKey(null)} />
          ) : (
            <>
              {section === "overview" && (!data ? <Loader /> : (
                <div className="kw-fade space-y-6" data-testid="control-overview">
                  <ControlHero tasks={data.tasks} portalCount={data.portal_count} totals={data.totals} userName={user.name} t={t} />
                  <KPIRow kpis={data.kpis} t={t} />
                  <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                    <div className="xl:col-span-2">
                      <PortalHealthMatrix portals={data.portals} t={t} onRow={openDetail} />
                    </div>
                    <div className="space-y-6">
                      <TaskCenter tasks={data.tasks} t={t} onGoto={select} />
                      <SecurityPanel security={data.security} t={t} onGoto={() => select("security")} />
                    </div>
                  </div>
                  <ActivityTimeline items={activity} t={t} compact />
                </div>
              ))}

              {section === "portals" && (!data ? <Loader /> : (
                <div className="kw-fade" data-testid="control-portals">
                  <h1 className="mb-6 font-heading text-3xl font-bold tracking-tight text-white">{t("nav.portals")}</h1>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {data.portals.map((p, i) => (
                      <RichPortalCard key={p.portal_key} p={p} i={i} t={t}
                        onDetail={() => openDetail(p.portal_key)} onOpen={() => navigate(`/p/${p.portal_key}`)} />
                    ))}
                  </div>
                </div>
              ))}

              {section === "activity" && (
                <div className="kw-fade" data-testid="control-activity">
                  <h1 className="mb-6 font-heading text-3xl font-bold tracking-tight text-white">{t("control.recentActivity")}</h1>
                  <ActivityTimeline items={activity} t={t} />
                </div>
              )}
              {section === "documents" && (
                <div className="kw-fade" data-testid="control-documents-section">
                  <h1 className="mb-2 font-heading text-3xl font-bold tracking-tight text-white">{t("control.documents")}</h1>
                  <p className="mb-6 text-sm text-zinc-400">{t("control.documentsSub")}</p>
                  <ControlDocuments portals={data?.portals || []} />
                </div>
              )}

              {section === "readiness" && (
                <div className="kw-fade" data-testid="control-readiness">
                  <h1 className="mb-6 font-heading text-3xl font-bold tracking-tight text-white">{t("nav.readiness")}</h1>
                  <ProductionHealthMatrix />
                </div>
              )}
              {section === "users" && <UsersManager />}
              {section === "approvals" && <CrossApprovals />}
              {section === "reports" && <ControlReports />}
              {section === "automations" && <EmailStatus />}
              {section === "security" && <SecurityLog />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
